import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ModalController, AlertController, LoadingController, IonInput } from '@ionic/angular/lazy';
import { CashTrackingService } from 'src/app/services/cash-tracking.service';
import { SupportedCurrencyService } from 'src/app/services/supported-currency.service';
import { StorageService } from 'src/app/services/storage.service';
import { RegisterService } from 'src/app/services/register.service';
import { Decimal } from 'decimal.js';

@Component({
    selector: 'app-cash-tracking-add-cash',
    templateUrl: './cash-tracking-add-cash.page.html',
    styleUrls: ['./cash-tracking-add-cash.page.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class CashTrackingAddCashPage implements OnInit {

    @ViewChild('amount_input', { static: false }) amount_input: IonInput;

    spinner = null;
    loading_spinner = null;
    loading = false;
    register_id = null;
    register = null;
    supported_currencies = null;

    params = {
        currency_id: null,
        price: null,
        note: null
    };

    currency_options: any = { header: 'Currency' };

    constructor(
        public modalController: ModalController,
        public alertController: AlertController,
        private cashTrackingService: CashTrackingService,
        private supportedCurrencyService: SupportedCurrencyService,
        public loadingController: LoadingController,
        private storageService: StorageService,
        private registerService: RegisterService
    ) { }

    ngOnInit() {

        this.loadData().then(async () => {

            if(this.supported_currencies.length > 1) {

                this.storageService.get("register_id").then((register_id) => {

                    if(register_id) {

                        this.register_id = register_id;

                        this.registerService.getRegister(this.register_id).then((register) => {

                            if(register) {

                                this.register = register;

                                for(let i = 0; i < this.supported_currencies.length; i++) {

                                    // NOTE: default currency
                                    if(this.supported_currencies[i].id == this.register['location'].default_currency.id) {
                                        let register_location_default_currency = this.supported_currencies.splice(i, 1)[0];
                                        this.supported_currencies.unshift(register_location_default_currency);
                                        this.params.currency_id = register_location_default_currency.id;
                                        break;
                                    }

                                }
                            }
                        })
                        .catch(() => { });
                    }
                })
                .catch(() => { });

            // NOTE: if only one currency is supported, select it automatically
            }
            else {
                this.params.currency_id = this.supported_currencies[0].id;
            }

        })
        .catch(() => { });

    }

    loadData() {
        return new Promise<void>((resolve, reject) => {
            this.presentLoadingSpinner("Loading currencies").then(() => {
                this.supportedCurrencyService.getSupportedCurrencies().then((supported_currencies: any) => {
                    if(supported_currencies) {
                        this.supported_currencies = supported_currencies;
                        this.supported_currencies.sort((a, b) => {
                            if (a['title'].toLowerCase() < b['title'].toLowerCase()) { return -1; }
                            if (a['title'].toLowerCase() > b['title'].toLowerCase()) { return 1; }
                            return 0;
                        });
                        this.loading_spinner.dismiss();
                        resolve();
                    }
                })
                .catch(() => {
                    this.loading_spinner.dismiss();
                    reject();
                });
            });
        });
    }

    ionViewDidEnter() {

        // NOTE: we use a timeout here so the DOM gets a chance to load before setting focus
        setTimeout(() => {
            if(this.amount_input) {
                this.amount_input.setFocus();
            }
        }, 200);

    }

    close() {
        this.modalController.dismiss();
    }

    add() {

        let validated = true;
        let price_regex = /^\d+(\.\d+)?$/;

        // catch empty currency
        if(this.params.currency_id == null || this.params.currency_id.trim() === "") {
            this.presentAlert("Missing currency", "You need to set a currency.");
            validated = false;
        }
        // catch empty amount
        else if(this.params.price == null || this.params.price.trim() === "") {
            this.presentAlert("Missing amount", "You need to set an amount.");
            validated = false;
        }
        // catch letters or symbols or multiple dps in amount
        else if(!price_regex.test(String(this.params.price).toLowerCase())) {
            this.presentAlert("Invalid amount", "The amount is invalid.");
            validated = false;
        }

        if(validated) {

            this.presentSpinner("Adding cash").then(() => {

                // ensure 2dp
                this.params.price = Number(new Decimal(this.params.price).toDecimalPlaces(2));

                // prevent empty note
                if(this.params.note == null || this.params.note.trim() === "") {
                    this.params.note = null;
                }

                this.cashTrackingService.addCashMovement(this.params).then(async () => {
                    await this.spinner.dismiss();
                    await this.modalController.dismiss();
                })
                .catch(() => {
                    this.spinner.dismiss();
                });

            });
        }
    }

    async presentAlert(header, message) {
        const alert = await this.alertController.create({
            header: header,
            message: message,
            cssClass:'alert',
            buttons: ['OK']
        });
        await alert.present();
    }

    async presentLoadingSpinner(message) {
        this.loading = true;
        this.loading_spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        this.loading_spinner.onDidDismiss().then(() => {
            this.loading = false;
        });
        await this.loading_spinner.present();
    }

    async presentSpinner(message) {
        this.spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        await this.spinner.present();
    }

}
